import { useEffect, useRef, useState } from 'react'
import { Link, NavLink, Outlet, useLocation } from 'react-router-dom'
import {
  Boxes,
  ChevronDown,
  KeyRound,
  Briefcase,
  Car,
  Package,
  UserPlus,
  ClipboardCheck,
  UserCheck,
  Calendar,
  CheckSquare,
  ClipboardList,
  Building2,
  UserCog,
  ListChecks,
  FileText,
  LayoutDashboard,
  LogOut,
  Menu,
  X,
  Plane,
  Settings,
  ShieldAlert,
  Users,
  UsersRound,
} from 'lucide-react'
import { useAuth, useLogout } from '@/hooks/useAuth'
import { cn } from '@/lib/utils'
import { NotificationBell } from '@/components/NotificationBell'
import { useIsDeptHead } from '@/hooks/useRecruitment'
import type { UserRole } from '@/lib/types'

type Nav = { to: string; label: string; icon: typeof LayoutDashboard; adminOnly?: boolean; roles?: UserRole[]; deptHead?: boolean }

type NavSection = { label: string; items: Nav[] }

const NAV_SECTIONS: NavSection[] = [
  {
    label: 'Overview',
    items: [
      { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { to: '/approvals', label: 'My approvals', icon: ListChecks },
    ],
  },
  {
    label: 'Requests & cases',
    items: [
      { to: '/requests', label: 'Requests', icon: ClipboardList },
      { to: '/incidents', label: 'Incidents', icon: ShieldAlert },
      { to: '/tasks', label: 'Tasks', icon: CheckSquare },
    ],
  },
  {
    label: 'People',
    items: [
      { to: '/employees', label: 'Employees', icon: Users, roles: ['admin', 'manager', 'handler'] },
      { to: '/team', label: 'HRAD Team', icon: UsersRound },
      { to: '/recruitment', label: 'Recruitment', icon: UserPlus, roles: ['admin', 'manager', 'handler'], deptHead: true },
      { to: '/onboarding', label: 'Onboarding', icon: ClipboardCheck, roles: ['admin', 'manager', 'handler'] },
      { to: '/my-onboarding', label: 'My onboarding', icon: UserCheck, roles: ['employee'] },
      { to: '/trainings', label: 'Trainings', icon: FileText },
    ],
  },
  {
    label: 'Workplace',
    items: [
      { to: '/calendar', label: 'Calendar', icon: Calendar },
      { to: '/meeting-rooms', label: 'Meeting Rooms', icon: Briefcase },
      { to: '/travel', label: 'Travel', icon: Plane },
      { to: '/vehicles', label: 'Vehicles', icon: Car },
    ],
  },
  {
    label: 'Assets',
    items: [
      { to: '/assets', label: 'Assets', icon: Boxes, roles: ['admin', 'manager', 'handler'] },
      { to: '/assets/mine', label: 'My assets', icon: Package },
    ],
  },
  {
    label: 'Admin',
    items: [
      { to: '/departments', label: 'Departments', icon: Building2, adminOnly: true },
      { to: '/users', label: 'Users and roles', icon: UserCog, adminOnly: true },
      { to: '/reports', label: 'Reports', icon: FileText },
      { to: '/settings', label: 'Settings', icon: Settings },
    ],
  },
]

function BrandMark() {
  return (
    <span className="flex items-center gap-2">
      <span
        aria-hidden="true"
        className="flex h-8 w-8 items-center justify-center rounded-lg bg-wegen-600 text-sm font-bold text-white"
      >
        W
      </span>
      <span className="text-base font-semibold tracking-tight text-slate-900">WeGen HRAD</span>
    </span>
  )
}

function NavItems({ sections, onNavigate }: { sections: NavSection[]; onNavigate?: () => void }) {
  return (
    <nav className="flex-1 space-y-5 overflow-y-auto p-3">
      {sections.map((section) => (
        <div key={section.label}>
          <div className="px-3 pb-1 text-[11px] font-medium tracking-wide text-slate-400">{section.label}</div>
          <div className="space-y-0.5">
            {section.items.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                end={to === '/assets'}
                onClick={onNavigate}
                className={({ isActive }) =>
                  cn(
                    'relative flex items-center gap-3 rounded-md px-3 py-2 text-sm transition',
                    isActive
                      ? 'bg-wegen-50 font-medium text-wegen-700 before:absolute before:inset-y-1 before:left-0 before:w-[3px] before:rounded-full before:bg-wegen-600'
                      : 'text-slate-700 hover:bg-slate-100',
                  )
                }
              >
                <Icon className="h-4 w-4" />
                {label}
              </NavLink>
            ))}
          </div>
        </div>
      ))}
    </nav>
  )
}

function initialsOf(name: string): string {
  const base = name.includes('@') ? name.split('@')[0].replace(/[._-]+/g, ' ') : name
  const parts = base.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  const first = parts[0][0]
  const last = parts.length > 1 ? parts[parts.length - 1][0] : ''
  return (first + last).toUpperCase()
}

function UserMenu() {
  const { user } = useAuth()
  const logout = useLogout()
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const display = user?.full_name ?? user?.email ?? ''
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-label="User menu"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 rounded-lg p-1 hover:bg-slate-100"
      >
        <span className="flex h-8 w-8 items-center justify-center rounded-full bg-wegen-100 text-xs font-semibold text-wegen-700">
          {initialsOf(display)}
        </span>
        <span className="hidden text-left sm:block">
          <span className="block max-w-[10rem] truncate text-sm font-medium leading-tight text-slate-900">{display}</span>
          <span className="block text-xs capitalize leading-tight text-slate-500">{user?.role}</span>
        </span>
        <ChevronDown className="hidden h-4 w-4 text-slate-400 sm:block" aria-hidden="true" />
      </button>
      {open ? (
        <div
          role="menu"
          className="absolute right-0 top-full z-40 mt-2 w-48 rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
        >
          <Link
            role="menuitem"
            to="/account/password"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            <KeyRound className="h-4 w-4" />
            Change password
          </Link>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false)
              logout.mutate()
            }}
            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
          >
            <LogOut className="h-4 w-4" />
            Sign out
          </button>
        </div>
      ) : null}
    </div>
  )
}

export function Layout() {
  const { user } = useAuth()
  const logout = useLogout()
  const { isDeptHead } = useIsDeptHead()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const { pathname } = useLocation()

  useEffect(() => {
    setDrawerOpen(false)
  }, [pathname])

  useEffect(() => {
    if (!drawerOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setDrawerOpen(false)
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [drawerOpen])
  const isVisible = (n: Nav) => {
    if (n.adminOnly && user?.role !== 'admin') return false
    if (!n.roles) return true
    if (user && n.roles.includes(user.role)) return true
    return Boolean(n.deptHead && isDeptHead)
  }
  const visible = NAV_SECTIONS.map((sec) => ({ ...sec, items: sec.items.filter(isVisible) })).filter(
    (sec) => sec.items.length > 0,
  )

  return (
    <div className="flex h-full min-h-screen overflow-x-hidden bg-slate-50">
      <aside className="hidden w-60 shrink-0 border-r border-slate-200 bg-white md:flex md:flex-col">
        <div className="flex h-16 items-center border-b border-slate-200 px-5">
          <BrandMark />
        </div>
        <NavItems sections={visible} />
      </aside>

      {drawerOpen ? (
        <div className="fixed inset-0 z-40 md:hidden">
          <div
            data-testid="nav-backdrop"
            className="absolute inset-0 bg-slate-900/40"
            onClick={() => setDrawerOpen(false)}
          />
          <aside
            id="mobile-nav"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation"
            className="absolute inset-y-0 left-0 flex w-64 max-w-[85vw] flex-col bg-white shadow-xl"
          >
            <div className="flex h-14 items-center justify-between border-b border-slate-200 px-4">
              <BrandMark />
              <button
                type="button"
                aria-label="Close menu"
                onClick={() => setDrawerOpen(false)}
                className="rounded p-1 text-slate-500 hover:bg-slate-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <NavItems sections={visible} onNavigate={() => setDrawerOpen(false)} />
            <div className="border-t border-slate-200 p-3 text-xs text-slate-600">
              <div className="truncate font-medium text-slate-900">{user?.full_name ?? user?.email}</div>
              <div className="mt-0.5 capitalize">{user?.role}</div>
              <button
                type="button"
                onClick={() => logout.mutate()}
                className="mt-3 inline-flex items-center gap-2 text-xs text-slate-600 hover:text-slate-900"
              >
                <LogOut className="h-3.5 w-3.5" />
                Sign out
              </button>
            </div>
          </aside>
        </div>
      ) : null}

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-slate-200 bg-white px-4 md:h-16 md:px-6">
          <div className="flex items-center gap-2">
            <button
              type="button"
              aria-label="Open menu"
              aria-expanded={drawerOpen}
              aria-controls="mobile-nav"
              onClick={() => setDrawerOpen(true)}
              className="rounded p-1.5 text-slate-600 hover:bg-slate-100 md:hidden"
            >
              <Menu className="h-5 w-5" />
            </button>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <UserMenu />
          </div>
        </header>
        {user?.must_change_password ? (
          <div role="alert" className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-900 md:px-6">
            You are using a temporary password.{' '}
            <Link to="/account/password" className="font-medium underline">
              Change your password
            </Link>{' '}
            to continue securely.
          </div>
        ) : null}
        <main className="min-w-0 flex-1 overflow-y-auto p-4 md:p-6">
          <div className="mx-auto w-full max-w-screen-2xl">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}
